import i18n from '@/utils/i18n';
import React from 'react';
import {
  AvailableOrderFulfillmentFieldsFragment,
  NewOrder_OrderFieldsFragment,
} from '@/types';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { FulfillmentActionSheetFulfillmentDaysView } from './fulfillment-action-sheet-fulfillment-days-view';
import { FulfillmentActionSheetOptionsView } from './fulfillment-action-sheet-options-view';
import {
  FulfillmentActionSheetRoute,
  useFulfillmentPicker,
} from './use-fulfillment-picker';
import { FulfillmentActionSheetTimeslotView } from './fulfillment-action-sheet-timeslot-view';
import { SCREEN_PADDING } from '@/styles';
import { SelectedFulfillmentType } from '@/contexts/new-order-actions-context-types';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { TimeslotOption } from '@domain/fulfillment-settings/types/types';
import { TrackingProperties } from '@/utils/analytics/base-reporter';

export { FulfillmentActionSheetRoute } from './use-fulfillment-picker';

interface Props {
  draftOrder: NewOrder_OrderFieldsFragment | null;
  fulfillmentOptions: AvailableOrderFulfillmentFieldsFragment[];
  initialRoute?: FulfillmentActionSheetRoute;
  initialSelectedFulfillment: SelectedFulfillmentType;
  initialSelectedFulfillmentOption?: AvailableOrderFulfillmentFieldsFragment;
  initialTimeslotOptions?: TimeslotOption[];
  isLoading: boolean;
  onClose: () => void;
  onOpen?: () => void;
  onSave: (selectedFulfillment: SelectedFulfillmentType) => Promise<void>;
  popoverTarget?: View | null;
  renderAsPopover?: boolean;
  trackingProperties: TrackingProperties | null;
}

export const FulfillmentActionSheet: React.FC<Props> = ({
  initialRoute,
  draftOrder,
  fulfillmentOptions,
  initialSelectedFulfillment,
  initialSelectedFulfillmentOption,
  initialTimeslotOptions,
  isLoading,
  onClose,
  onOpen,
  onSave,
  trackingProperties,
  renderAsPopover,
  popoverTarget,
}) => {
  const { height } = useWindowDimensions();

  const {
    clearTimeslot,
    defaultInitialRoute,
    listData,
    resetSelection,
    saveFulfillment,
    saveFulfillmentForToday,
    savingFulfillment,
    selectedFulfillmentDate,
    selectedFulfillmentOption,
    selectedTimeslot,
    setSelectedFulfillmentDate,
    setSelectedFulfillmentOption,
    setSelectedTimeslot,
    setTimeslotOptionsForDay,
    timeslotOptionsForDay,
    timeslotRouteTitle,
  } = useFulfillmentPicker({
    draftOrder,
    fulfillmentOptions,
    initialSelectedFulfillment,
    initialSelectedFulfillmentOption,
    initialTimeslotOptions,
    onClose,
    onOpen,
    onSave,
  });

  return (
    <CombinedActionSheet
      animateTransitions
      contentStyle={[
        styles.contentReset,
        { maxHeight: Math.floor(height * 0.9) },
        renderAsPopover ? styles.contentAsPopover : null,
      ]}
      hasStickyHeader={false}
      initialRoute={initialRoute || defaultInitialRoute}
      isFocusableForKeyboardShortcuts
      onClose={onClose}
      onRouteChange={(nextKey) => {
        if (nextKey === FulfillmentActionSheetRoute.Base) {
          resetSelection();
        }
      }}
      popoverTarget={popoverTarget}
      renderAsPopover={renderAsPopover}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case FulfillmentActionSheetRoute.Base:
            return (
              <FulfillmentActionSheetOptionsView
                isLoading={isLoading}
                listData={listData}
                onSelect={(option) => {
                  const { fulfillmentOption, nextRouteKey: nextSelectedView } =
                    option.metadata!;

                  if (nextSelectedView) {
                    // Animate to the selected view if the fulfillment option has one
                    setSelectedFulfillmentOption(fulfillmentOption);
                    jumpTo(nextSelectedView);
                  } else {
                    saveFulfillmentForToday(fulfillmentOption, onClose);
                  }
                }}
                savingFulfillment={savingFulfillment}
              />
            );
          case FulfillmentActionSheetRoute.DaysView:
            if (!selectedFulfillmentOption) {
              return null;
            }
            return (
              <FulfillmentActionSheetFulfillmentDaysView
                clearTimeslot={clearTimeslot}
                draftOrder={draftOrder}
                fulfillmentOption={selectedFulfillmentOption}
                initialFulfillmentDate={selectedFulfillmentDate}
                isSaving={Boolean(savingFulfillment)}
                jumpToTimeslots={(timeslotOptions: TimeslotOption[]) => {
                  setTimeslotOptionsForDay(timeslotOptions);
                  jumpTo(FulfillmentActionSheetRoute.TimeslotSelect);
                }}
                onDayChange={setSelectedFulfillmentDate}
                onSave={(dateMoment, timeslot, resolvedFulfillmentOption) => {
                  saveFulfillment(
                    {
                      date: dateMoment.toISOString(),
                      fulfillmentSettingsUUID:
                        resolvedFulfillmentOption.fulfillment_settings_id,
                      timeslot,
                      type: resolvedFulfillmentOption.fulfillment_name,
                    },
                    onClose,
                  );
                }}
                selectedTimeslot={selectedTimeslot}
                trackingProperties={trackingProperties}
              />
            );
          case FulfillmentActionSheetRoute.TimeslotSelect:
            if (!timeslotOptionsForDay || !selectedFulfillmentOption) {
              return null;
            }

            return (
              <FulfillmentActionSheetTimeslotView
                onSelect={(timeslot) => {
                  setSelectedTimeslot(timeslot);
                  jumpTo(FulfillmentActionSheetRoute.DaysView);
                }}
                timeslotOptions={timeslotOptionsForDay}
              />
            );
          default:
            return null;
        }
      }}
      routes={{
        [FulfillmentActionSheetRoute.Base]: {
          previousKey: null,
          showSmallLayoutTitle: true,
          title: i18n.t('fulfillment_select'),
        },
        [FulfillmentActionSheetRoute.DaysView]: {
          contentStyle: { paddingVertical: 16 },
          previousKey:
            fulfillmentOptions.length > 1
              ? FulfillmentActionSheetRoute.Base
              : null,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('fulfillment_date_select'),
        },
        [FulfillmentActionSheetRoute.TimeslotSelect]: {
          previousKey: FulfillmentActionSheetRoute.DaysView,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: timeslotRouteTitle,
        },
      }}
      titleStyle={styles.title}
    />
  );
};

const styles = StyleSheet.create({
  contentAsPopover: {
    marginTop: 14,
  },
  contentReset: {
    paddingHorizontal: 0,
  },
  title: {
    marginLeft: SCREEN_PADDING,
  },
});
